import { Attribute, Relationship, Resource } from 'json-api';
import { Validable } from 'src/app/core/common-behaviors/validate.mixin';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { WithMessages } from '../common-behaviors/with-messages.mixin';
import { BusinessUnit } from './business-unit.resource';
import { Firm } from './firm.resource';
import { Offer } from './offer.resource';
import { Product } from './product.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  NumberFilter,
  StringFilter,
  ResourceListFilter,
} from 'src/app/filter/filter';
import { AtomService } from '../atom.service';
import {
  BusinessunitConfig,
  FirmConfig,
} from 'src/app/shared/tools/search/search-config';
import { Message } from './message.resource';

@Resource({
  type: 'services',
})
export class Service extends AtomResource {
  @Attribute() name?: Translatable;
  @Attribute({ key: 'commercial_name' }) commercialName?: Translatable;
  @Attribute() validity?: string;
  @Attribute({ key: 'validity_tz' }) validityTz?: string;

  @Relationship() businessunits?: BusinessUnit[];
  @Relationship() ownerfirm?: Firm;
  @Relationship() products?: Product[];
  @Relationship() messages?: Message[];

  get offers(): Offer[] | undefined {
    return this.products?.reduce((offers, product) => {
      if (!product.offers) {
        throw new Error('Missing product offers');
      }
      return offers.concat(...product.offers);
    }, [] as Offer[]);
  }
}

export class ServiceConfig implements ResourceConfig {
  tables = {
    includes: [],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'validity',
        header: 'Validity',
        displayed: true,
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
          {
            path: ['commercialName'],
            class: 'mat-body-1',
          },
        ],
        header: 'Name / Commercial Name',
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'name',
      },
      {
        columnDef: 'products',
        header: 'Products',
        displayed: true,
        link: {
          resource: 'products',
          list: 'products',
          target: 'service',
        },
      },
      {
        columnDef: 'offers',
        header: 'Offers',
        displayed: true,
        link: {
          resource: 'offers',
          list: 'offers',
          target: 'product.service',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'shopping_basket',
      title: [
        {
          path: ['name'],
          translatable: false,
        },
      ],
      id: true,
      validity: true,
    },
    elements: [
      {
        list: [
          {
            icon: 'badge',
            title: 'Commercial Name',
            APIKey: 'commercialName',
            value: {
              path: ['commercialName'],
            },
          },
          {
            icon: 'business',
            title: 'Owner Firm',
            APIKey: 'ownerfirm',
            value: {
              path: ['ownerfirm.name'],
            },
            link: {
              resource: 'firms',
              filter: 'ownerfirm.id',
            },
          },
        ],
      },
      {
        title: 'Business Unit',
        list: [
          {
            type: 'chips',
            APIKey: 'businessunits',
            value: {
              path: ['name'],
            },
          },
        ],
      },
      {
        title: 'Products',
        list: [
          {
            // type: 'list',
            icon: 'public',
            APIKey: 'products',
            value: {
              path: ['country.id', 'name'],
            },
            link: {
              resource: 'products',
              list: 'products',
              target: 'service',
            },
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Service',
        formGroup: 'service',
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            APIKey: 'name',
            translatable: true,
            required: true,
          },
          {
            type: 'text',
            title: 'Commercial Name',
            formControl: 'commercialName',
            APIKey: 'commercialName',
            translatable: true,
            required: true,
          },
          {
            type: 'resource',
            title: 'Business Units',
            formControl: 'businessunits',
            APIKey: 'businessunits',
            search: new BusinessunitConfig(true),
            multiple: true,
            required: false,
            noCols: true,
          },
          {
            type: 'validity',
            title: 'Validity',
            formControl: 'validity',
            required: true,
          },
          {
            type: 'resource',
            title: 'Owner Firm',
            formControl: 'ownerfirm',
            search: new FirmConfig(this.atom),
            required: true,
            noCols: true,
          },
        ],
      },
    ],
  };
  resource = Service;
  resourceName?: string | undefined = 'services';
  resourceKey?: string | undefined = 'services';
  resourceKeySingle?: string | undefined = 'service';
  includes?: string[] | undefined = [
    'businessunits',
    'ownerfirm',
    'products.country',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name', true),
    new ResourceListFilter(
      this.atom,
      BusinessUnit,
      'businessunits.id',
      'Business unit'
    ),
    new NumberFilter('ownerfirm.id', 'Owner Firm ID'),
    new StringFilter('ownerfirm.name', 'Owner Firm Name'),
    new StringFilter('products.country.id', 'Country ID'),
    new StringFilter('products.country.name', 'Country Name'),
  ];
  constructor(private atom: AtomService) {}
}
